
import { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { generateSpiralParticles } from "../../utils/globeUtils";

interface ParticleRingProps {
  size: number;
}

const ParticleRing = ({ size }: ParticleRingProps) => {
  const particleCount = 2000;
  const particles = useMemo(() => {
    return generateSpiralParticles(particleCount, size * 1.05);
  }, [size, particleCount]);
  
  const pointsGeometry = useMemo(() => {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);
    
    particles.forEach((point, i) => {
      positions[i * 3] = point[0];
      positions[i * 3 + 1] = point[1];
      positions[i * 3 + 2] = point[2];
      
      // Create blue to cyan color gradient
      const intensity = 0.5 + Math.random() * 0.5;
      colors[i * 3] = 0.1 * intensity; // R
      colors[i * 3 + 1] = 0.5 * intensity; // G
      colors[i * 3 + 2] = 1.0 * intensity; // B
    });
    
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    
    return geometry;
  }, [particles, particleCount]);
  
  // Create animated point cloud
  const pointsRef = useRef<THREE.Points>(null);
  
  useFrame(({ clock }) => {
    if (pointsRef.current) {
      pointsRef.current.rotation.y = clock.getElapsedTime() * 0.05;
    }
  });
  
  return (
    <points ref={pointsRef}>
      <primitive object={pointsGeometry} attach="geometry" />
      <pointsMaterial
        size={0.05}
        vertexColors
        transparent
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
};

export default ParticleRing;
